import { useState, useMemo } from 'react';
import { T } from '../theme';
import { ContentHeader, Tabs, Button, Badge } from './fmxkit';
import { updateProfileRole, deleteUserViaEdgeFunction, createUserViaEdgeFunction, setUserFeature } from '../db';
import PromptsAdminTab from './PromptsAdminTab';
import CorrectionsAdminTab from './CorrectionsAdminTab';
import RunsAdminTab from './RunsAdminTab';
import FieldRulesAdminTab from './FieldRulesAdminTab';
import QbrAdminTab from './QbrAdminTab';

// Admin console — full-screen replacement for the old AdminPanelModal.
// Hosts the existing admin tab components under FMX tabs; the Users tab
// (user/role/feature management) is ported from the modal with its logic
// intact. Reached from the sidebar ADMIN group or the user menu.

const ADMIN_TABS = [
  { id: 'users',       label: 'Users' },
  { id: 'prompts',     label: 'Prompts' },
  { id: 'runs',        label: 'AI runs' },
  { id: 'corrections', label: 'Corrections' },
  { id: 'fields',      label: 'Field rules' },
  { id: 'qbr',         label: 'QBR config' },
];

const TAB_SUBTITLES = {
  users:       'Roles, feature access, and account management',
  prompts:     'Admin-editable prompts (PDF extraction + CSV field mapping)',
  runs:        'Every prompt invocation, logged',
  corrections: 'User corrections across PDF + CSV flows',
  fields:      'Override required-flag defaults per entity/field',
  qbr:         'QBR narrative prompt + per-metric formula reference',
};

// Optional tools that admins grant per-user via profiles.feature_permissions.
// Keep keys stable — they are the contract with hasFeature() callers.
const FEATURE_FLAGS = [
  { key: 'equipment_ocr',        label: 'Existing Attachment Field Scan' },
  { key: 'qbr_generator',        label: 'QBR builder' },
  { key: 'data_import',          label: 'Data import' },
  { key: 'custom_field_builder', label: 'AI Form Builder' },
];

export default function AdminScreen({
  currentUser, currentProfile, allProfiles, projects,
  onProfilesChanged, fieldOverrides, onFieldOverridesChanged,
  initialTab = 'users', tab, onTabChange,
}) {
  // Controlled when `tab` is provided (lets the caller sync sidebar
  // highlighting); falls back to internal state otherwise.
  const [internalTab, setInternalTab] = useState(
    ADMIN_TABS.some(t => t.id === initialTab) ? initialTab : 'users'
  );
  const activeTab = tab ?? internalTab;
  const setActiveTab = (id) => { if (onTabChange) onTabChange(id); else setInternalTab(id); };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ContentHeader
        crumbs={['Admin', ADMIN_TABS.find(t => t.id === activeTab)?.label || '']}
        title="Admin console"
        subtitle={TAB_SUBTITLES[activeTab]}
      />
      <Tabs
        tabs={ADMIN_TABS}
        current={activeTab}
        onChange={setActiveTab}
        style={{ background: T.white, padding: '0 24px' }}
      />
      <div style={{ flex: 1, overflow: 'auto', background: T.bg, padding: '20px 24px' }}>
        <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 360 }}>
          {activeTab === 'users' && (
            <UsersAdminTab
              currentUser={currentUser}
              allProfiles={allProfiles}
              projects={projects}
              onProfilesChanged={onProfilesChanged}
            />
          )}
          {activeTab === 'prompts' && <PromptsAdminTab currentUserId={currentUser.id} />}
          {activeTab === 'runs' && <RunsAdminTab allProfiles={allProfiles} currentUserId={currentUser.id} />}
          {activeTab === 'corrections' && <CorrectionsAdminTab currentUserId={currentUser.id} />}
          {activeTab === 'fields' && (
            <FieldRulesAdminTab
              fieldOverrides={fieldOverrides || {}}
              onFieldOverridesChanged={onFieldOverridesChanged}
              currentUserId={currentUser.id}
            />
          )}
          {activeTab === 'qbr' && <QbrAdminTab currentUserId={currentUser.id} />}
        </div>
      </div>
    </div>
  );
}

// ---- Users tab (ported from AdminPanelModal; logic unchanged) ----
function UsersAdminTab({ currentUser, allProfiles, projects, onProfilesChanged }) {
  const [busy, setBusy] = useState(null); // userId currently being operated on
  const [errorMsg, setErrorMsg] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Add-user form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [newPassword, setNewPassword] = useState('');

  const resetAddForm = () => {
    setNewEmail(''); setNewFullName(''); setNewRole('user'); setNewPassword('');
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setAddBusy(true);
    const result = await createUserViaEdgeFunction({
      email: newEmail.trim(),
      fullName: newFullName.trim(),
      role: newRole,
      password: newPassword,
    });
    if (result?.error) {
      setErrorMsg(result.error);
    } else {
      await onProfilesChanged?.();
      resetAddForm();
      setShowAddForm(false);
    }
    setAddBusy(false);
  };

  // Count admins to prevent demoting the last one
  const adminCount = useMemo(
    () => allProfiles.filter(p => p.role === 'admin').length,
    [allProfiles]
  );

  // Count projects per user
  const projectCountByUser = useMemo(() => {
    const map = {};
    for (const p of projects || []) {
      if (p.user_id) map[p.user_id] = (map[p.user_id] || 0) + 1;
    }
    return map;
  }, [projects]);

  const handleFeatureToggle = async (userId, key, enabled) => {
    setErrorMsg('');
    setBusy(userId);
    const result = await setUserFeature(userId, key, enabled);
    if (!result) setErrorMsg('Could not update feature permission.');
    await onProfilesChanged?.();
    setBusy(null);
  };

  const handleRoleChange = async (userId, role) => {
    setErrorMsg('');
    setBusy(userId);
    const result = await updateProfileRole(userId, role);
    if (!result) setErrorMsg('Could not update role. You may not have permission.');
    await onProfilesChanged?.();
    setBusy(null);
  };

  const handleDelete = async (userId) => {
    setErrorMsg('');
    setBusy(userId);
    const result = await deleteUserViaEdgeFunction(userId);
    if (result?.error) {
      setErrorMsg(result.error);
    } else {
      await onProfilesChanged?.();
    }
    setDeleteConfirmId(null);
    setBusy(null);
  };

  // Sort: current user first, then admins, then by name
  const sorted = useMemo(() => {
    return [...allProfiles].sort((a, b) => {
      if (a.id === currentUser.id) return -1;
      if (b.id === currentUser.id) return 1;
      if (a.role === 'admin' && b.role !== 'admin') return -1;
      if (b.role === 'admin' && a.role !== 'admin') return 1;
      return (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '');
    });
  }, [allProfiles, currentUser.id]);

  const hs = (w, center, right) => ({
    textAlign: right ? 'right' : center ? 'center' : 'left',
    font: `700 12px/16px ${T.fontSans}`, color: T.inkMuted, textTransform: 'uppercase',
    letterSpacing: '0.04em', padding: '9px 14px', background: T.bgMuted,
    borderBottom: `1px solid ${T.borderSubtle}`, width: w, whiteSpace: 'nowrap',
  });
  const cs = { padding: '11px 14px', borderBottom: `1px solid ${T.borderSubtle}`, font: `400 14px/20px ${T.fontSans}`, color: T.ink, verticalAlign: 'middle' };

  const addLabel = { display: 'block', font: `500 11px ${T.fontSans}`, color: T.inkMuted, marginBottom: 3 };
  const addInput = {
    width: '100%', padding: '6px 9px', fontSize: 13, borderRadius: T.radiusBtn,
    border: `1px solid ${T.borderStrong}`, outline: 'none', boxSizing: 'border-box',
    background: T.white, fontFamily: T.fontSans,
  };

  return (
    <>
      {/* Toolbar */}
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.borderSubtle}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ font: `400 13px ${T.fontSans}`, color: T.inkMuted }}>{allProfiles.length} users</span>
        <Button variant={showAddForm ? 'neutral' : 'primary'} size="sm" icon={showAddForm ? undefined : 'plus'} onClick={() => { setErrorMsg(''); setShowAddForm(v => !v); }}>
          {showAddForm ? 'Cancel' : 'Add user'}
        </Button>
      </div>

      {/* Add-user form */}
      {showAddForm && (
        <form
          onSubmit={handleAddUser}
          style={{ padding: '14px 16px', background: T.bg, borderBottom: `1px solid ${T.borderSubtle}`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}
        >
          <div>
            <label style={addLabel}>Full name</label>
            <input type="text" required value={newFullName} onChange={e => setNewFullName(e.target.value)} placeholder="Jane Smith" style={addInput} />
          </div>
          <div>
            <label style={addLabel}>Email</label>
            <input type="email" required value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="jane@example.com" style={addInput} />
          </div>
          <div>
            <label style={addLabel}>Role</label>
            <select value={newRole} onChange={e => setNewRole(e.target.value)} style={addInput}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label style={addLabel}>Temporary password</label>
            <input type="text" required minLength={8} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 8 characters" style={addInput} />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <Button variant="neutral" size="sm" type="button" disabled={addBusy} onClick={() => { resetAddForm(); setShowAddForm(false); setErrorMsg(''); }}>Cancel</Button>
            <Button variant="primary" size="sm" type="submit" disabled={addBusy}>{addBusy ? 'Creating…' : 'Create user'}</Button>
          </div>
        </form>
      )}

      {/* Error */}
      {errorMsg && (
        <div style={{ padding: '10px 16px', background: T.tint.error, borderBottom: `1px solid ${T.badge.red}`, color: T.red500, font: `400 13px ${T.fontSans}` }}>
          {errorMsg}
        </div>
      )}

      {/* Users grid */}
      <div style={{ overflowX: 'auto', flex: 1 }}>
        <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={hs()}>User</th>
              <th style={hs(110)}>Role</th>
              <th style={hs()} title="Per-user access to optional tools. Admins implicitly have all features.">Features</th>
              <th style={hs(80, true)}>Projects</th>
              <th style={hs(200, false, true)} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((p, i) => {
              const isSelf = p.id === currentUser.id;
              const isLastAdmin = p.role === 'admin' && adminCount <= 1;
              const busyThis = busy === p.id;
              const showDeleteConfirm = deleteConfirmId === p.id;

              return (
                <tr key={p.id} style={{ background: i % 2 ? '#fcfcfc' : T.white }}>
                  {/* User */}
                  <td style={cs}>
                    <div style={{ fontWeight: 600, color: T.ink, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {p.full_name || '—'}
                      {isSelf && <Badge color="blueLight">You</Badge>}
                      {p.role === 'admin' && <Badge color="blueDark">Admin</Badge>}
                    </div>
                    <div style={{ font: `400 12px ${T.fontSans}`, color: T.inkSoft, marginTop: 2 }}>{p.email || '—'}</div>
                  </td>

                  {/* Role */}
                  <td style={cs}>
                    <select
                      value={p.role || 'user'}
                      onChange={e => handleRoleChange(p.id, e.target.value)}
                      disabled={busyThis || (isSelf && isLastAdmin)}
                      title={isSelf && isLastAdmin ? 'Cannot demote the last admin' : ''}
                      style={{
                        fontSize: 13, padding: '4px 8px', borderRadius: T.radiusBtn,
                        border: `1px solid ${T.borderStrong}`, background: T.white, fontFamily: T.fontSans,
                        cursor: (busyThis || (isSelf && isLastAdmin)) ? 'not-allowed' : 'pointer',
                        opacity: (isSelf && isLastAdmin) ? 0.5 : 1,
                      }}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>

                  {/* Features */}
                  <td style={cs}>
                    {FEATURE_FLAGS.map(f => {
                      const isAdmin = p.role === 'admin';
                      const enabled = isAdmin || !!(p.feature_permissions || {})[f.key];
                      return (
                        <label key={f.key} title={isAdmin ? 'Admins have all features' : f.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, font: `400 12px ${T.fontSans}`, color: T.inkMuted, marginRight: 12, cursor: isAdmin ? 'not-allowed' : 'pointer', opacity: isAdmin ? 0.55 : 1, whiteSpace: 'nowrap' }}>
                          <input
                            type="checkbox"
                            checked={enabled}
                            disabled={busyThis || isAdmin}
                            onChange={e => handleFeatureToggle(p.id, f.key, e.target.checked)}
                          />
                          {f.label}
                        </label>
                      );
                    })}
                  </td>

                  {/* Project count */}
                  <td style={{ ...cs, textAlign: 'right', fontFamily: T.fontData, fontWeight: 600, color: (projectCountByUser[p.id] || 0) ? T.ink : T.border }}>
                    {projectCountByUser[p.id] || '—'}
                  </td>

                  {/* Actions */}
                  <td style={{ ...cs, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {isSelf ? (
                      <span style={{ font: `400 12px ${T.fontSans}`, color: T.inkSoft, fontStyle: 'italic' }}>—</span>
                    ) : showDeleteConfirm ? (
                      <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                        {(projectCountByUser[p.id] || 0) > 0 && (
                          <span style={{ font: `400 11px ${T.fontSans}`, color: T.orange500, fontStyle: 'italic', maxWidth: 220, textAlign: 'right' }}>
                            Owns {projectCountByUser[p.id]} project{projectCountByUser[p.id] === 1 ? '' : 's'} — will become unassigned
                          </span>
                        )}
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <Button variant="destructive" size="sm" disabled={busyThis} onClick={() => handleDelete(p.id)}>{busyThis ? 'Deleting…' : 'Yes, delete'}</Button>
                          <Button variant="neutral" size="sm" disabled={busyThis} onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <Button variant="tertiary" size="sm" icon="trash-can" style={{ color: T.red500 }} onClick={() => setDeleteConfirmId(p.id)}>Delete</Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
